import $ from 'jquery';
import Marionette from 'backbone.marionette';
import Cookie from '@component/common/src/util/Cookie';
import ExperimentationSDK from '@component/experiment';
import TagManagerUtil from '@component/common/src/util/TagManagerUtil';
import ClientSideStorage from '@component/common/src/util/ClientSideStorage';
import ObjectUtil from '@component/common/src/util/ObjectUtil';
import PageApp from 'pageApp';
import QuickBagTemplate from '../templates/partials/quick-bag/quickBagREST.hbs';
import QuickBagProductView from './QuickBagProduct';
import EmptyQuickBag from './EmptyQuickBag';
import qbActions from '../components/QuickBagActions';
import QuickBagAnalytics from '../components/QuickBagAnalytics';

/* istanbul ignore next */
const Channel = PageApp.channel ? PageApp.channel : PageApp.default.channel;

const QuickBagCompositeView = Marionette.CompositeView.extend({
  template: QuickBagTemplate,
  childView: QuickBagProductView,
  emptyView: EmptyQuickBag,
  initialize(options) {
    this.viewType = options.viewType || 'Default';
    this.listenTo(this.model, 'change', this.onRender);
  },

  childViewContainer() {
    if (!this.isEmpty()) {
      return 'ul.bagItems';
    }
    return 'div.quickBagEmpty';
  },

  ui: {
    quickbagSubTotal: '#quickBagSubTotal a',
    checkoutButton: '.checkoutButton a',
    srNotification: '.sr_notification',
    quickBagCheckout: '#qbCheckout',
    qbViewBagLink: '#qbViewBagLink',
    qbViewBagCheckout: '#qbViewBagCheckout',
  },

  events: {
    'click @ui.quickbagSubTotal': 'onBagLinkClick',
    'click @ui.checkoutButton': 'onCheckoutButtonClick',
    'click @ui.srNotification': 'onSrNotificationClick',
    'click @ui.quickBagCheckout': 'onQuickBagCheckout',
    'click @ui.qbViewBagLink': 'onQuickBagViewBag',
    'click @ui.qbViewBagCheckout': 'onQuickBagViewBag',
  },

  // set loadingError in options of child view, if the parent model has loadingError
  // to indicate the fetch call is failed.
  emptyViewOptions() {
    if (this.model.get('loadingError')) {
      return {
        loadingError: true,
      };
    }
    return {};
  },

  // ###Method - isEmpty()  Marionette inbuilt method, overriding to check for the desired collection object
  // > returns
  // >
  // + True if the given array is empty else false
  isEmpty() {
    const bagItems = this.model.get('bagItems');
    return !((bagItems && bagItems.length));
  },

  // ###Method - onChildModelDestory()  called by child view once the product is deleted from the model and updates the moddel and collection
  // > parameters
  // >
  // + *data* - the updated model data from child view
  // > returns
  // >
  // + NONE
  onChildModelDestory(_data) {
    const data = _data;
    data.srBalance = this.model.attributes.srBalance || '';
    Cookie.set('CartItem', data.meta.totalQuantity, 'GCs');
    this.model.clear({
      silent: true,
    }).set(this.model.parse(data));
    this.model.setCacheData(data);
    Channel.trigger(qbActions.UPDATE_COUNT, data.meta.totalQuantity);
  },

  onRender() {
    if (this.model.get('isSkipBagOnQuickBagEnabled')) {
      let href;
      if (typeof $('#m-bag-count-anchor').attr === 'function') {
        href = $('#m-bag-count-anchor').attr('href');
      }
      // this is required as we want to use the same bag url which header uses
      if (href && typeof this.ui.qbViewBagLink.attr === 'function') {
        this.ui.qbViewBagLink.attr('href', href);
      }
    }
  },
  // ###Method - childViewOptions() marionette inbuilt method to provide data to the child view once it is initialized.
  childViewOptions() {
    return {
      parentView: this,
    };
  },

  // ###Method - modelEvents() marionette inbuilt method to listen to the model events to bind various actions
  modelEvents: {
    change() {
      const bagItems = this.model.get('bagItems');
      // we do not want to parse an empty array. and hence we are having an empty array.
      if (bagItems && bagItems.length) {
        const { properties } = this.model.get('meta');
        if (properties.itemAvailabilityDisplayLAM && properties.itemAvailabilityDisplayLAMQuickBagEnabled) {
          import(/* webpackChunkName: "QbLamView" */ './QbLamView').then((_Lam) => {
            let Lam = _Lam.default ? _Lam.default : _Lam;
            Lam = new Lam({
              response: this.model.attributes,
              qbrest: true,
            });
            this.collection.reset(Lam.options.response.bagItems, {
              parse: true,
            });
            this.model.setCacheData(Lam.options.response);
          });
        } else {
          this.collection.reset(bagItems, {
            parse: true,
          });
        }
      } else {
        // just resetting the collection when bagItems is empty
        this.collection.reset();
      }
      this.render();
    },
  },

  onBagLinkClick() {
    QuickBagAnalytics.fireElementTag('your bag link');
  },

  onCheckoutButtonClick() {
    QuickBagAnalytics.fireElementTag('checkout button');
  },
  onQuickBagCheckout(event) {
    event.preventDefault();
    this.model.quickCheckout()
      .done((response) => {
        const offerAppliedInExpressCheckout = ObjectUtil.getChildProperty(response, 'ResponseEntity.body.meta.bagAttributes.offerAppliedInExpressCheckout');
        if (offerAppliedInExpressCheckout) {
          const promoCode = ObjectUtil.getChildProperty(response, 'ResponseEntity.body.meta.bagAttributes.expressCheckoutPromoCode');
          Cookie.set('PromoCodeOne', promoCode, 'SMISCGCs');
          // TODO Add tagging
          // this.fireApplyPromoTag();
        }
        let checkoutError = ObjectUtil.getChildProperty(response, 'ResponseEntity.body.cookies.checkoutError');
        if (checkoutError) {
          checkoutError = decodeURIComponent(checkoutError);
          Cookie.set('checkoutError', checkoutError);
        }
        const url = ObjectUtil.getChildProperty(response, 'ResponseEntity.headers.Location[0]');
        this.redirectToPage(url);
      })
      .fail(() => {
        this.redirectToPage('/my/bag/');
      });
    TagManagerUtil.fireTag('link', { event_name: 'minibag checkout' });
  },
  onQuickBagViewBag() {
    TagManagerUtil.fireTag('link', { event_name: 'minibag view bag' });
  },
  redirectToPage(redirectUrl) {
    let url = redirectUrl;
    if (url === '/chkout/startcheckout') {
      const login365Enabled = ObjectUtil.getChildProperty(this, 'model.attributes.meta.login365Enabled');
      const ts = new Date().getTime();

      const recipes = ['442-21'];
      ExperimentationSDK.getSelectedRecipesForExperiment(recipes).always((resp) => {
        if (resp && resp[0] === '442-21') {
          const lidParam = 'lid=rbag_standard_checkout_cta';
          url += (url.indexOf('?') > -1) ? '&' : '?';
          url += `${lidParam}`;
        }
      });

      // Bust the browser disk-cache everytime /chkout/startcheckout is called.
      url += (url.indexOf('?') > -1) ? `&ts=${ts}` : `?ts=${ts}`;

      if (login365Enabled) {
        const deviceFingerPrint = ClientSideStorage.getPersistent('login365FingerPrint');
        url += (url.indexOf('?') > -1) ? '&' : '?';
        url += `deviceFingerPrint=${deviceFingerPrint}`;
      }
    }
    this.redirectToHref(url);
  },
  redirectToHref(newHref) {
    window.location.href = newHref;
  },
  onSrNotificationClick() {
    QuickBagAnalytics.fireManualLinkTagForStarRewards();
    TagManagerUtil.fireTag('link', {
      link_page_name: 'shopping bag | star rewards',
    });
  },
});

export default QuickBagCompositeView;
